import type { Order } from "@/types/supabase";

// Lib
import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { fetchWithAuth } from "@/lib/api";
import { getAccessTokenDirect } from "@/lib/auth0";
import { getTodayRangeInElSalvador } from "@/lib/utils";
import { listOrders, getOrdersCounts } from "@/services/ordersClient";
import { getOrderDetail } from "@/services/ordersClient";

// Hooks
import { usePermissions } from "@/contexts/PermissionContext";

/**
 * FetchOptions and PermissionFilters remain unchanged
 */
interface FetchOptions {
  status?: string;
  branchId?: number;
  brandId?: number;
  trackingId?: string;
}

interface PermissionFilters {
  canAccessAll: boolean;
  accessibleBrandIds: number[];
  accessibleBranchIds: number[];
}

interface UseOrdersStore {
  isLoading: boolean;
  isSearching: boolean;
  isDetailLoading: boolean;
  pageSize: number;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  orderDuration: number | null;
  hasInitiallyFetched: boolean;
  paginatedOrdersCurrentDay: Order[];
  currentDayOrdersCounts: Order[];
  ordersCount: Record<string, number>;
  fetchOptions: FetchOptions;
  fetchPaginatedOrdersCurrentDay: (
    page?: number,
    options?: FetchOptions,
  ) => Promise<void>;
  fetchCurrentDayOrdersCounts: () => Promise<void>;
  setCurrentPage: (page: number) => void;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  statusFilter: "all" | Order["status"];
  setStatusFilter: (status: "all" | Order["status"]) => void;
  branchFilter: number | "all";
  setBranchFilter: (branchId: number | "all") => void;
  brandFilter: number | "all";
  setBrandFilter: (brandId: number | "all") => void;
  setOrderDuration: (duration: number | null) => void;
  trackingIdFilter: string;
  setTrackingIdFilter: (trackingId: string) => void;
  clearAllFilters: () => void;
  refreshCurrentPage: () => void;
  // Internal permission handling
  _applyPermissionFilters: (
    query: any,
    permissionFilters: PermissionFilters,
  ) => any;
  _getPermissionFilters: () => PermissionFilters;
  _resolveBranchIdsFromBusinessIds: (brandIds: number[]) => Promise<number[]>;
  orderDetail: Order | null;
  orderNotFound: boolean;
  setOrderDetail: (orderId: number | null) => void;
  setOrderDetailDirect: (order: Order | null) => void;
  fetchOrderById: (orderId: number) => Promise<void>;
  removeSelectedOrder: () => void;
  // For performance: selector for OrderTable to minimize re-renders
  getOrderTableSlice: () => {
    paginatedOrdersCurrentDay: Order[];
    isLoading: boolean;
    isSearching: boolean;
    currentPage: number;
    totalPages: number;
    totalCount: number;
    statusFilter: "all" | Order["status"];
    branchFilter: number | "all";
    brandFilter: number | "all";
    trackingIdFilter: string;
    ordersCount: Record<string, number>;
  };
}

export const useOrdersStore = create<UseOrdersStore>((set, get) => ({
  isLoading: true,
  isSearching: false,
  isDetailLoading: false,
  pageSize: 10,
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  hasInitiallyFetched: false,
  paginatedOrdersCurrentDay: [],
  currentDayOrdersCounts: [],
  ordersCount: {},
  fetchOptions: {},
  orderDetail: null,
  orderNotFound: false,
  orderDuration: null,
  setOrderDuration: (duration: number | null) => {
    set({ orderDuration: duration });
  },
  setOrderDetail: (orderId: number | null) => {
    if (orderId) {
      const matchingOrder = get().paginatedOrdersCurrentDay.find(
        (order) => order.id === orderId,
      );
      if (matchingOrder) {
        set({ orderDetail: matchingOrder });
      } else {
        set({ orderDetail: null });
      }
    } else {
      set({ orderDetail: null });
    }
  },

  setOrderDetailDirect: (order: Order | null) => {
    set({ orderDetail: order });
  },
  fetchOrderById: async (orderId: number) => {
    try {
      set({ isDetailLoading: true, orderNotFound: false });
      const detail = await getOrderDetail(orderId);
      const data = (detail as any)?.data || detail || null;
      if (data) {
        set({ orderDetail: data as Order, orderNotFound: false });
      } else {
        set({ orderDetail: null, orderNotFound: true });
      }
    } catch (e) {
      set({ orderDetail: null, orderNotFound: true });
    } finally {
      set({ isDetailLoading: false });
    }
  },
  removeSelectedOrder: () => {
    set({ orderDetail: null });
  },

  // Internal permission handling
  _getPermissionFilters: () => {
    // This will be overridden by the permission-aware hook
    return {
      canAccessAll: true,
      accessibleBrandIds: [],
      accessibleBranchIds: [],
    };
  },

  _resolveBranchIdsFromBusinessIds: async (
    brandIds: number[],
  ): Promise<number[]> => {
    try {
      // Fetch branches via REST API (no Supabase) using existing auth utilities
      const token = await getAccessTokenDirect();
      const collectedIds = new Set<number>();

      // API supports filtering by a single brandId; fetch per brand and merge
      for (const brandId of brandIds) {
        const url = `/branch/branches?page=1&limit=1000&brandId=${brandId}`;
        const response = await fetchWithAuth(url, token);
        const items = (response?.data as Array<{ id: number }>) || [];
        for (const item of items) {
          if (typeof item?.id === "number") collectedIds.add(item.id);
        }
      }

      return Array.from(collectedIds);
    } catch (error) {
      return [];
    }
  },

  _applyPermissionFilters: (
    query: any,
    permissionFilters: PermissionFilters,
  ) => {
    if (!permissionFilters.canAccessAll) {
      const { accessibleBranchIds, accessibleBrandIds } = permissionFilters;

      if (accessibleBranchIds.length > 0) {
        query = query.in("branch_id", accessibleBranchIds);
      } else if (accessibleBrandIds.length > 0) {
        query = query.in("brand_id", accessibleBrandIds);
      } else {
        // No access to any branches
        query = query.eq("id", -1);
      }
    }
    return query;
  },

  setCurrentPage: (page: number) => {
    set({ currentPage: page });
    get().fetchPaginatedOrdersCurrentDay(page, get().fetchOptions);
  },

  goToNextPage: () => {
    const { currentPage, totalPages } = get();
    if (currentPage < totalPages) {
      get().setCurrentPage(currentPage + 1);
    }
  },

  goToPrevPage: () => {
    const { currentPage } = get();
    if (currentPage > 1) {
      get().setCurrentPage(currentPage - 1);
    }
  },

  fetchPaginatedOrdersCurrentDay: async (page = 1, options?: FetchOptions) => {
    // Determine if this is a search operation
    const isSearchOperation =
      options?.trackingId && options.trackingId.trim() !== "";

    // Always reset both loading states before fetch
    set({ isLoading: false, isSearching: false });

    if (isSearchOperation) {
      set({ isSearching: true, isLoading: false });
    } else {
      set({ isLoading: true, isSearching: false });
    }

    try {
      const {
        pageSize,
        statusFilter,
        branchFilter,
        brandFilter,
        trackingIdFilter,
      } = get();
      // If no options provided, use stored filter values
      if (!options) {
        options = {
          status: statusFilter !== "all" ? statusFilter : undefined,
          branchId: branchFilter !== "all" ? branchFilter : undefined,
          brandId: brandFilter !== "all" ? brandFilter : undefined,
          trackingId:
            trackingIdFilter.trim() !== "" ? trackingIdFilter : undefined,
        };
      }

      // Build query params
      const params = new URLSearchParams();
      params.append("page", String(page));
      params.append("pageSize", String(pageSize));

      // For current day, set startDate and endDate to today
      const now = new Date();
      const startDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        0,
        0,
        0,
        0,
      );
      const endDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999,
      );

      params.append("startDate", startDate.toISOString());
      params.append("endDate", endDate.toISOString());

      if (options.brandId) params.append("brandId", String(options.brandId));
      if (options.branchId) params.append("branchId", String(options.branchId));
      if (options.status && options.status !== "all")
        params.append("status", String(options.status));
      if (options.trackingId && options.trackingId.trim() !== "")
        params.append("searchTerm", options.trackingId.trim());

      const { from, to } = getTodayRangeInElSalvador();
      const result = await listOrders({
        page,
        pageSize,
        startDate: from,
        endDate: to,
        brandId: options.brandId,
        branchId: options.branchId,
        status: options.status,
        searchTerm: options.trackingId,
      });
      const data = result?.data || [];
      const totalCount = result?.total || 0;
      const totalPages = result?.totalPages || Math.ceil(totalCount / pageSize);

      set(() => ({
        paginatedOrdersCurrentDay: data,
        currentPage: page,
        totalCount,
        totalPages,
        hasInitiallyFetched: true,
      }));
    } catch (error) {
      // Handle error silently
    } finally {
      // Always reset both loading states after fetch
      set({ isSearching: false, isLoading: false });
    }
  },

  fetchCurrentDayOrdersCounts: async () => {
    try {
      const { branchFilter, brandFilter } = get();
      const result = await getOrdersCounts({
        brandId: brandFilter !== "all" ? (brandFilter as number) : undefined,
        branchId: branchFilter !== "all" ? (branchFilter as number) : undefined,
      });
      const ordersCount: Record<string, number> = { ...(result || {}) };
      ordersCount.all = Object.values(ordersCount).reduce(
        (sum, v) => sum + (typeof v === "number" ? v : 0),
        0,
      );

      set({ ordersCount });
    } catch (error) {
      // Handle error silently
    }
  },

  statusFilter: "all",
  setStatusFilter: (status: "all" | Order["status"]) => {
    const newFetchOptions = {
      ...get().fetchOptions,
      status: status === "all" ? undefined : status,
    };
    set({
      statusFilter: status,
      fetchOptions: newFetchOptions,
      currentPage: 1,
    });
    get().fetchPaginatedOrdersCurrentDay(1, newFetchOptions);
  },

  branchFilter: "all",
  setBranchFilter: (branchId: number | "all") => {
    const newFetchOptions = {
      ...get().fetchOptions,
      branchId: branchId === "all" ? undefined : branchId,
    };
    set({
      branchFilter: branchId,
      fetchOptions: newFetchOptions,
      currentPage: 1,
    });
    get().fetchPaginatedOrdersCurrentDay(1, newFetchOptions);
  },

  brandFilter: "all",
  setBrandFilter: (brandId: number | "all") => {
    const newFetchOptions = {
      ...get().fetchOptions,
      brandId: brandId === "all" ? undefined : brandId,
    };
    set({
      brandFilter: brandId,
      fetchOptions: newFetchOptions,
      currentPage: 1,
    });
    get().fetchPaginatedOrdersCurrentDay(1, newFetchOptions);
  },

  trackingIdFilter: "",
  setTrackingIdFilter: (trackingId: string) => {
    const newFetchOptions = {
      ...get().fetchOptions,
      trackingId: trackingId.trim() === "" ? undefined : trackingId.trim(),
    };
    set({
      trackingIdFilter: trackingId,
      fetchOptions: newFetchOptions,
      currentPage: 1,
    });
    get().fetchPaginatedOrdersCurrentDay(1, newFetchOptions);
  },

  clearAllFilters: () => {
    set({
      statusFilter: "all",
      branchFilter: "all",
      brandFilter: "all",
      trackingIdFilter: "",
      fetchOptions: {},
      currentPage: 1,
    });
    get().fetchPaginatedOrdersCurrentDay(1, {});
  },

  // Function to refresh current page data (useful for realtime updates)
  refreshCurrentPage: () => {
    const { currentPage, fetchOptions } = get();
    get().fetchPaginatedOrdersCurrentDay(currentPage, fetchOptions);
  },

  // Selector for OrderTable to minimize re-renders
  getOrderTableSlice: () => {
    const {
      paginatedOrdersCurrentDay,
      isLoading,
      isSearching,
      currentPage,
      totalPages,
      totalCount,
      statusFilter,
      branchFilter,
      brandFilter,
      trackingIdFilter,
      ordersCount,
    } = get();
    return {
      paginatedOrdersCurrentDay,
      isLoading,
      isSearching,
      currentPage,
      totalPages,
      totalCount,
      statusFilter,
      branchFilter,
      brandFilter,
      trackingIdFilter,
      ordersCount,
    };
  },
}));

// Hook to initialize permissions in the store
export const useInitializeOrderPermissions = () => {
  const { getAccessibleBrands, getAccessibleBranches, resourceAccess } =
    usePermissions();

  // Memoize the accessible businesses and branches to create stable dependencies
  const accessibleBrands = useMemo(
    () => getAccessibleBrands(),
    [getAccessibleBrands],
  );
  const accessibleBranches = useMemo(
    () => getAccessibleBranches(),
    [getAccessibleBranches],
  );

  // Create stable string representations for dependency comparison
  const accessibleBrandsString = useMemo(
    () => accessibleBrands.join(","),
    [accessibleBrands],
  );
  const accessibleBranchesString = useMemo(
    () => accessibleBranches.join(","),
    [accessibleBranches],
  );

  useEffect(() => {
    useOrdersStore.setState({
      _getPermissionFilters: () => ({
        canAccessAll: resourceAccess.canAccessAll,
        accessibleBrandIds: accessibleBrands,
        accessibleBranchIds: accessibleBranches,
      }),
    });
  }, [
    resourceAccess.canAccessAll,
    accessibleBrandsString,
    accessibleBranchesString,
    accessibleBrands,
    accessibleBranches,
  ]);
};

/**
 * --- Additional Guidance for Consumers ---
 *
 * 1. To avoid excessive re-renders in OrderTable, use:
 *    const orderTableState = useOrdersStore((state) => state.getOrderTableSlice());
 *    This will subscribe only to the relevant slice.
 *
 * 2. To replace deprecated onKeyPress, use onKeyDown and check event.key === "Enter":
 *    <input onKeyDown={e => e.key === "Enter" && handleSearch()} ... />
 *    (Remove all onKeyPress usage in your components.)
 */
